import { SvgIcon, ToggleButton, ToggleButtonGroup } from '@mui/material';
import {
  RiArrowLeftLine,
  RiCarFill,
  RiLoader3Fill,
  RiLuggageCartFill,
  RiRunFill,
} from '@remixicon/react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import useQueryErrorNotifier from '../../../hooks/useQueryErrorNotifier';
import { PublicIsland } from '../../../types/island';
import { PublicIslandJourney } from '../types';
import JourneyList from './JourneyList';
import { groupJourneysByTab, JourneyGroup } from './utils';
import SectionHeader from '../../../components/SectionHeader';

export interface FerrytimetablePageProps {
  island: PublicIsland;
  journeys: PublicIslandJourney[];
}

const IslandFerryTimetablePage = ({
  island,
  journeys,
}: FerrytimetablePageProps) => {
  const { t, language } = useLanguage();
  const [loadTime, setLoadTime] = useState('');

  const groupedJourneys = useMemo(
    () => groupJourneysByTab(journeys),
    [journeys],
  );

  const [activeTab, setActiveTab] = useState<JourneyGroup>(() =>
    groupedJourneys.passengers.length > 0
      ? 'passengers'
      : groupedJourneys.vehicles.length > 0
        ? 'vehicles'
        : 'cargo',
  );

  useEffect(() => {
    // Every time the journey information changes, the load time is set
    setLoadTime(
      new Date().toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: false,
      }),
    );
  }, [journeys]);

  useEffect(() => {
    // Ensure that there is always a visible active tab
    if (groupedJourneys[activeTab].length === 0) {
      const newActiveTab: JourneyGroup =
        groupedJourneys.passengers.length > 0
          ? 'passengers'
          : groupedJourneys.vehicles.length > 0
            ? 'vehicles'
            : groupedJourneys.cargo.length > 0
              ? 'cargo'
              : 'passengers';
      if (groupedJourneys[newActiveTab].length > 0) {
        setActiveTab(newActiveTab);
      }
    }
  }, [activeTab, groupedJourneys]);

  const noJourneys = !journeys.length;

  return (
    <div className="grid grid-rows-[min-content_1fr] h-full pb-4">
      <SectionHeader
        className="mb-8"
        belowContent={
          !noJourneys && (
            <ToggleButtonGroup
              exclusive
              value={activeTab}
              onChange={(_, newValue) => {
                setActiveTab(
                  newValue != null ? (newValue as JourneyGroup) : 'passengers',
                );
              }}
            >
              {groupedJourneys.passengers.length > 0 && (
                <ToggleButton value="passengers">
                  <span className="flex gap-4 items-center justify-around">
                    <span>{t('content.passengers')}</span>
                    <SvgIcon
                      color="inherit"
                      component={RiRunFill}
                      className="size-[16px]"
                    />
                  </span>
                </ToggleButton>
              )}
              {groupedJourneys.vehicles.length && (
                <ToggleButton value="vehicles">
                  <span className="flex gap-4 items-center justify-around">
                    <span>{t('content.vehicles')}</span>
                    <SvgIcon
                      color="inherit"
                      component={RiCarFill}
                      className="size-[16px]"
                    />
                  </span>
                </ToggleButton>
              )}
              {groupedJourneys.cargo.length && (
                <ToggleButton value="cargo">
                  <span className="flex gap-4 items-center justify-around">
                    <span>{t('content.cargo')}</span>
                    <SvgIcon
                      color="inherit"
                      component={RiLuggageCartFill}
                      className="size-[16px]"
                    />
                  </span>
                </ToggleButton>
              )}
            </ToggleButtonGroup>
          )
        }
      >
        <div className="grid grid-cols-[1fr_auto] items-center gap-4">
          <div className="flex gap-3 md:gap-4 items-center">
            <div>
              <nav className="lg:translate-x-[-22px]">
                <Link
                  to="/"
                  className="inline-flex gap-[4px] items-center h-[24px] font-medium underline underline-offset-2"
                >
                  <RiArrowLeftLine className="h-[18px] w-[18px]" />
                  <span className="underline underline-offset-2">
                    {t('content.back-to-homepage')}
                  </span>
                </Link>
              </nav>
              <h1 className="text-5xl mb-2">{island.names[language]}</h1>
              <h2 className="text-l font-medium">
                {`${t('content.today-sailings')} ${loadTime}`}
              </h2>
            </div>
          </div>
          <img
            title={t('content.drcdg-title')}
            className='h-[82px] hidden md:block'
            src="/images/drcdg_std_colour.png"
          />
        </div>
      </SectionHeader>
      <div className="h-full">
        <div className="main-content">
          {noJourneys ? (
            <div className="flex items-center justify-center">
              <h1 className="text-4xl font-bold">
                {t('content.no-journeys-found-for-today')}
              </h1>
            </div>
          ) : !groupedJourneys[activeTab].length ? (
            <div className="flex items-center justify-center min-h-[60vh]">
              <h1 className="text-4xl font-bold text-center">
                {t('content.no-journeys-for-service-type')}
              </h1>
            </div>
          ) : (
            <JourneyList
              journeys={groupedJourneys[activeTab]}
              island={island}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const IslandFerryTimetablePageWrapper = () => {
  const { islandId } = useParams();
  const { t } = useLanguage();
  const { getPublicIslands, getPublicJourneysByIslandId } = useIotsApi();

  const {
    data: islands = [],
    isLoading: isLoadingIslands,
    error: islandsError,
  } = useQuery({
    queryKey: ['public-islands'],
    queryFn: () => getPublicIslands(),
    refetchOnMount: 'always',
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const {
    data: journeys = [],
    isLoading: isLoadingJourneys,
    error: journeysError,
  } = useQuery({
    queryKey: ['public-journeys', islandId ?? ''],
    queryFn: () => getPublicJourneysByIslandId(islandId ?? ''),
  });

  useQueryErrorNotifier([islandsError, journeysError]);

  if (isLoadingIslands || isLoadingJourneys) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <RiLoader3Fill className="animate-spin size-8" />
      </div>
    );
  }

  const island = islands.find(({ id }) => id === islandId);

  if (!island) {
    return (
      <>
        <div className="main-content min-h-[56px] flex items-center justify-start">
          <nav className="py-2">
            <Link to="/" className="inline-flex gap-1 items-center h-[24px]">
              <RiArrowLeftLine className="h-[18px] w-[18px]" />
              <span className="underline underline-offset-2">
                {t('content.back-to-islands')}
              </span>
            </Link>
          </nav>
        </div>
        <div className="flex justify-center items-center min-h-[60vh]">
          <h1 className="text-4xl font-bold">
            {t('content.invalid-islandid')}
          </h1>
        </div>
      </>
    );
  }

  return <IslandFerryTimetablePage island={island} journeys={journeys} />;
};

export default IslandFerryTimetablePageWrapper;
